import { Button } from '@/shadcn-ui/button';
import { useEffect, useRef, RefCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ArrowWithTailLeft from '@/icons/arrow-with-tail-left';
import ArrowWithTailRight from '@/icons/arrow-with-tail-right';
import { cn } from '@/shadcn-ui/utils';
import PhotoSphere from '@/common/components/photo-sphere';
import { VehicleImageWithMeta } from '@/catalog/types/vehicle';
import { createImageUrl } from '@/catalog/utils/pdp/create-image-url';
import Spacer from '@/common/components/spacer';
import Rule from '@/common/components/rule';

interface ImageGalleryCarouselProps {
  images: VehicleImageWithMeta[];
  interiorImages: VehicleImageWithMeta[];
  exteriorImages: VehicleImageWithMeta[];
  selectedImageIndex: number;
  setSelectedImageIndex: (index: number) => void;
  goToPrevious: () => void;
  goToNext: () => void;
  cdnUrl: string;
}

export default function ImageGalleryCarousel({
  images,
  interiorImages,
  exteriorImages,
  selectedImageIndex,
  setSelectedImageIndex,
  goToPrevious,
  goToNext,
  cdnUrl,
}: ImageGalleryCarouselProps) {
  // Refs are used to make sure thumbnail is scrolled into view during keyboard navigation
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const setThumbnailRef: RefCallback<HTMLButtonElement> = (
    element: HTMLButtonElement | null
  ) => {
    if (element) {
      thumbnailRefs.current[Number(element.getAttribute('data-index'))] = element;
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        goToPrevious();
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToNext, goToPrevious]);

  useEffect(() => {
    const selectedThumbnail = thumbnailRefs.current[selectedImageIndex];
    const scrollContainer = selectedThumbnail?.parentElement;

    if (selectedThumbnail && scrollContainer) {
      // Calculate the position that places the element at the bottom with 32px offset
      const newScrollTop =
        selectedThumbnail.offsetTop -
        scrollContainer.clientHeight +
        selectedThumbnail.offsetHeight +
        32;

      scrollContainer.scrollTo({
        top: Math.max(0, newScrollTop),
        behavior: 'smooth',
      });
    }
  }, [selectedImageIndex]);

  const selectedImage = images[selectedImageIndex];
  const isSelectedImageInterior360 = selectedImage.image_category === '360int';
  const selectedImageUrl = createImageUrl(cdnUrl, selectedImage);

  return (
    <div className="flex h-full w-full flex-row items-center gap-[16px]">
      <Button
        variant="clear"
        className="h-fit"
        onClick={goToPrevious}
        aria-label="Previous image"
      >
        <motion.div
          className="h-fit rounded-full border-[1px] border-solid border-flexcar-neutral100 p-[8px] transition-all hover:bg-flexcar-neutral100/20"
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.1 }}
        >
          <ArrowWithTailLeft />
        </motion.div>
      </Button>

      {/* Grid is necessary here to support the fade in/out motion animation */}
      <div className="grid h-full w-full">
        <AnimatePresence initial={false} mode="sync">
          {selectedImage && isSelectedImageInterior360 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'circInOut' }}
              className="col-start-1 row-start-1 h-full w-full rounded-[16px] shadow-shadow20"
            >
              <div className="h-full w-full">
                <PhotoSphere url={selectedImageUrl} fullscreenEventDelegate={() => {}} />
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={selectedImageUrl}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'circInOut' }}
              className="col-start-1 row-start-1 flex h-full w-full items-center justify-center overflow-hidden"
            >
              <img
                src={selectedImageUrl}
                alt="Selected vehicle"
                fetchPriority="high"
                loading="eager"
                className="h-auto max-h-full w-auto max-w-full rounded-[16px] object-contain shadow-shadow20"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Button
        variant="clear"
        className="h-fit"
        onClick={goToNext}
        aria-label="Next image"
      >
        <motion.div
          className="h-fit rounded-full border-[1px] border-solid border-flexcar-neutral100 p-[8px] transition-all hover:bg-flexcar-neutral100/20"
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.1 }}
        >
          <ArrowWithTailRight />
        </motion.div>
      </Button>

      {/* Thumbnail container */}
      <div className="scrollbar-hide ml-[40px] flex h-full flex-shrink-0 snap-y snap-mandatory flex-col gap-[16px] overflow-y-scroll p-[1px]">
        {exteriorImages?.length > 0 ? (
          <>
            <div className="flex w-fit flex-col">
              <p className="body-tight leading-[22px] tracking-[0.96px] text-flexcar-neutral100">
                Exterior
              </p>
              <Spacer variant="height8" />
              <Rule
                direction="horizontal"
                className="flex-shrink-0 bg-flexcar-neutral100"
              />
              <Spacer variant="height16" />
            </div>
            {exteriorImages.map((image) => {
              // Find index of this image in the main array for selection purposes
              const mainIndex = images.findIndex((img) => img === image);

              return (
                <Button
                  variant="clear"
                  key={`gallery-image-${image.image_type}`}
                  data-index={mainIndex}
                  ref={setThumbnailRef}
                  onClick={() => setSelectedImageIndex(mainIndex)}
                  className={cn(
                    'box-border flex h-fit flex-shrink-0 snap-center flex-col items-center justify-center rounded-[16px] border-[1px] border-solid border-transparent transition-all',
                    {
                      'border-flexcar-neutral100/90': selectedImageIndex === mainIndex,
                    }
                  )}
                >
                  <img
                    src={createImageUrl(cdnUrl, image)}
                    alt={`Gallery Vehicle ${image.image_type}`}
                    fetchPriority="high"
                    loading="eager"
                    className={cn(
                      'z-0 h-[120px] w-[180px] rounded-[16px] object-cover transition-all',
                      {
                        'opacity-30': selectedImageIndex === mainIndex,
                      }
                    )}
                  />
                </Button>
              );
            })}{' '}
          </>
        ) : null}

        {interiorImages?.length > 0 ? (
          <>
            <div className="flex w-fit flex-col">
              <Spacer variant="height32" />
              <p className="body-tight leading-[22px] tracking-[0.96px] text-flexcar-neutral100">
                Interior
              </p>
              <Spacer variant="height8" />
              <Rule
                direction="horizontal"
                className="flex-shrink-0 bg-flexcar-neutral100"
              />
              <Spacer variant="height16" />
            </div>

            {interiorImages.map((image) => {
              // Find index of this image in the main array for selection purposes
              const mainIndex = images.findIndex((img) => img === image);
              const isInterior360Thumb = image.image_category === '360int';

              return (
                <Button
                  variant="clear"
                  key={`gallery-image-${image.image_type}`}
                  data-index={mainIndex}
                  ref={setThumbnailRef}
                  onClick={() => setSelectedImageIndex(mainIndex)}
                  className={cn(
                    'box-border flex h-fit flex-shrink-0 snap-center flex-col items-center justify-center rounded-[16px] border-[1px] border-solid border-transparent transition-all',
                    {
                      'border-flexcar-neutral100/90': selectedImageIndex === mainIndex,
                      'group relative h-[120px] overflow-hidden': isInterior360Thumb,
                    }
                  )}
                >
                  <img
                    src={createImageUrl(cdnUrl, image)}
                    alt={`Gallery Vehicle ${image.image_type}`}
                    fetchPriority="high"
                    loading="eager"
                    className={cn(
                      'z-0 h-[120px] w-[180px] rounded-[16px] object-cover transition-all',
                      {
                        'absolute inset-0 blur-[8px] transition-all group-hover:blur-[2px]':
                          isInterior360Thumb,
                        'opacity-30': selectedImageIndex === mainIndex,
                      }
                    )}
                  />
                  {isInterior360Thumb ? (
                    <p className="text-flexcar-neutral20group-hover:bg-flexcar-neutral100 relative z-1 rounded-[32px] bg-flexcar-neutral100/90 px-[12px] py-[8px] font-sans-tight text-[10px] font-bold leading-[150%] tracking-[0.8px] group-hover:text-flexcar-neutral1">
                      360º INTERIOR
                    </p>
                  ) : null}
                </Button>
              );
            })}
          </>
        ) : null}
      </div>
    </div>
  );
}
